import { roleForParent } from '@tunasong/schemas'
import type { Agent, Entity, Persisted } from '@tunasong/schemas'
import type { TunaThunkDispatch } from '@tunasong/redux'
import { deterministicAgentId } from './deterministic-agent-id.js'
import { findAgents } from './find-agents.js'

export interface ResolveOrAttachAgentInput {
  parentId: string
  /** Definition id to match (e.g. 'song-collaborator'). */
  definitionId: string
  /** Injected for testing. Defaults to {@link findAgents}. */
  findAgents?: typeof findAgents
  /**
   * Injected for testing — in production this is
   * `({ parent, entity }) => dispatch(entitiesApi.endpoints.createEntity.initiate({ parent, entity })).unwrap()`.
   */
  createEntity: (input: { parent: Persisted<Entity> | null; entity: Entity }) => Promise<Persisted<Entity>>
  dispatch: TunaThunkDispatch
  getState: () => unknown
  /**
   * Pre-resolved parent entity. The caller passes it because the palette
   * command already has the song in `editor.rootElement`. Falls back to
   * null (root attach) if absent — which should not happen for the
   * palette flow.
   */
  parent?: Persisted<Entity> | null
}

const defaultAgentName = (definitionId: string): string => {
  switch (definitionId) {
    case 'song-collaborator':
      return 'Songwriting Collaborator'
    case 'chord-advisor':
      return 'Chord Advisor'
    case 'lyric-writer':
      return 'Lyric Writer'
    case 'songwriting-advisor':
      return 'Songwriting Advisor'
    default:
      return 'Agent'
  }
}

const resolveOrAttachAgentImpl = async ({
  parentId,
  definitionId,
  findAgents: findAgentsImpl = findAgents,
  createEntity,
  dispatch,
  getState,
  parent = null,
}: ResolveOrAttachAgentInput): Promise<Persisted<Agent>> => {
  const existing = await findAgentsImpl({ parentId, dispatch, getState })
  const match = existing.find(a => a.definitionId === definitionId)
  if (match) {
    return match
  }
  // Deterministic id (not server-assigned): see deterministic-agent-id.ts for
  // why — it's what makes offline-queue replays of this create
  // replay-idempotent instead of minting a duplicate advisor per replay.
  const id = await deterministicAgentId(parentId, definitionId)
  const newAgent = (await createEntity({
    parent,
    entity: {
      id,
      type: 'agent',
      parentId,
      definitionId,
      name: defaultAgentName(definitionId),
      // `role` and `config` are REQUIRED by agentSchema — omitting them made
      // the server reject every create from this path. The failed mutation
      // landed in the offline replay queue and the promise rejected, so the
      // Advisor layout rendered "Couldn't load the advisor" instead of the
      // composer. Mirrors the payload plugin-lib's
      // agent-definition-to-command.ts builds for the other create path.
      role: roleForParent(parent?.type),
      status: 'active',
      config: { proactivity: 'normal', triggers: { onChange: true } },
      memory: {},
    } as unknown as Entity,
  })) as Persisted<Agent>
  return newAgent
}

/**
 * In-flight dedupe: concurrent resolve calls for the same (parent, definition)
 * share one promise, so double-mounted effects (StrictMode) and rapid
 * re-invocations cannot race findAgents past each other into duplicate
 * createEntity calls. Keyed per parent+definition; cleared when settled.
 */
const inflight = new Map<string, Promise<Persisted<Agent>>>()

export const resolveOrAttachAgent = (input: ResolveOrAttachAgentInput): Promise<Persisted<Agent>> => {
  const key = `${input.parentId}:${input.definitionId}`
  const pending = inflight.get(key)
  if (pending) {
    return pending
  }
  const promise = resolveOrAttachAgentImpl(input).finally(() => inflight.delete(key))
  inflight.set(key, promise)
  return promise
}
